import pluralize from 'pluralize'

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatCurrency } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import type { PaymentPlan } from './quoteCardTypes'

const DENSE = 'h-auto px-0 py-1.5'

// Cents only where the carrier quotes them: a rounded installment misstates the draft, and its row stops adding up.
const money = (amount: number) =>
  formatCurrency(amount, { minimumFractionDigits: Number.isInteger(amount) ? 0 : 2 })

/** A carrier's payment plans side by side: what's due today, what follows, and what the year costs in total. */
export function PaymentPlansTable({ plans }: { plans: PaymentPlan[] }) {
  return (
    <Table className="border-ink-200 border-y text-xs">
      <TableHeader>
        <TableRow className="border-ink-100">
          {/* Labelled for screen readers only: the plan names say what this column is, so it can stay narrow. */}
          <TableHead className={cn(DENSE, 'w-px')}>
            <span className="sr-only">Plan</span>
          </TableHead>
          {/* Out of flow, so the heading spills left over the unlabelled column instead of widening its own. */}
          <TableHead className={cn(DENSE, 'relative text-right')}>
            <span className="absolute right-0 bottom-1.5">Down payment</span>
          </TableHead>
          <TableHead className={cn(DENSE, 'text-right')}>Then</TableHead>
          <TableHead className={cn(DENSE, 'text-right')}>Total</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {plans.map((plan) => (
          <TableRow key={plan.title} className="border-ink-100 hover:bg-transparent">
            <TableHead scope="row" className={cn(DENSE, 'w-px font-semibold')}>
              {plan.title}
            </TableHead>
            <TableCell className={cn(DENSE, 'text-foreground text-right')}>{money(plan.dueToday)}</TableCell>
            <TableCell className={cn(DENSE, 'text-muted-foreground text-right')}>
              {plan.installments ? (
                <>
                  <div>{pluralize('payment', plan.installments.count, true)} of</div>
                  <div>{money(plan.installments.amount)}</div>
                </>
              ) : (
                (plan.balance ?? '—')
              )}
            </TableCell>
            <TableCell className={cn(DENSE, 'text-foreground text-right')}>
              {plan.total === null ? '—' : money(plan.total)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
